/**
 * React Router v7 — data router configuration for veterans/shop.
 *
 * Pattern: createBrowserRouter with loader/action support.
 * Data loaders: used for route-level params only (slug extraction,
 *   auth guard redirects). TanStack Query remains the primary data layer
 *   for all server-state fetches — loaders do NOT replace useQuery.
 *
 * Auth guard: protected routes redirect to / if no session cookie is found.
 * The session check is fast (reads cookie) and runs before render.
 *
 * URL param pattern: use loader + useLoaderData() for :slug / :id params
 * instead of useParams() to keep component code clean.
 */

import { createBrowserRouter } from 'react-router'
import { RootLayout } from './RootLayout'
import { RouteError } from './components/vite/RouteError'

// Lazy-loaded pages — one chunk per route for fast initial load
const HomePage = () => import('./_pages/HomePage').then((m) => ({ Component: m.default }))
const ProductsPage = () =>
  import('./_pages/ProductsPage').then((m) => ({ Component: m.default }))
const ProductPage = () =>
  import('./_pages/ProductPage').then((m) => ({ Component: m.default }))
const CartPage = () => import('./_pages/CartPage').then((m) => ({ Component: m.default }))
const CheckoutPage = () =>
  import('./_pages/CheckoutPage').then((m) => ({ Component: m.default }))
const OrdersPage = () =>
  import('./_pages/OrdersPage').then((m) => ({ Component: m.default }))
const OrderDetailPage = () =>
  import('./_pages/OrderDetailPage').then((m) => ({ Component: m.default }))
const OrderConfirmPage = () =>
  import('./_pages/OrderConfirmPage').then((m) => ({ Component: m.default }))
const AdminCatalogPage = () =>
  import('./_pages/admin/AdminCatalogPage').then((m) => ({ Component: m.default }))
const AdminOrdersPage = () =>
  import('./_pages/admin/AdminOrdersPage').then((m) => ({ Component: m.default }))
const AdminOrderDetailPage = () =>
  import('./_pages/admin/AdminOrderDetailPage').then((m) => ({ Component: m.default }))
const AdminBYOMCatalogPage = () =>
  import('./_pages/admin/AdminBYOMCatalogPage').then((m) => ({ Component: m.default }))
const AdminElementsPage = () =>
  import('./_pages/admin/AdminElementsPage').then((m) => ({ Component: m.default }))
const AdminChannelsPage = () =>
  import('./_pages/admin/AdminChannelsPage').then((m) => ({ Component: m.default }))
// Legal pages
const PrivacyPage = () =>
  import('./_pages/legal/PrivacyPage').then((m) => ({ Component: m.default }))
const TermsPage = () =>
  import('./_pages/legal/TermsPage').then((m) => ({ Component: m.default }))
const RefundPage = () =>
  import('./_pages/legal/RefundPage').then((m) => ({ Component: m.default }))
const ShippingPage = () =>
  import('./_pages/legal/ShippingPage').then((m) => ({ Component: m.default }))
const ReturnsPage = () =>
  import('./_pages/legal/ReturnsPage').then((m) => ({ Component: m.default }))
const ConsentPage = () =>
  import('./_pages/ConsentPage').then((m) => ({ Component: m.default }))
const ExternalRedirectPage = () =>
  import('./_pages/ExternalRedirectPage').then((m) => ({ Component: m.default }))
const ContactPage = () =>
  import('./_pages/ContactPage').then((m) => ({ Component: m.default }))
const BYOMPage = () =>
  import('./_pages/BYOMPage').then((m) => ({ Component: m.default }))
const CatalogPage = () =>
  import('./_pages/CatalogPage').then((m) => ({ Component: m.default }))
const TrackPage = () =>
  import('./_pages/TrackPage').then((m) => ({ Component: m.default }))
const AccountPage = () =>
  import('./_pages/AccountPage').then((m) => ({ Component: m.default }))

export const router = createBrowserRouter([
  {
    path: '/',
    Component: RootLayout,
    ErrorBoundary: RouteError,
    children: [
      // ── Storefront ──────────────────────────────────────────────
      { index: true, lazy: HomePage },
      { path: 'products', lazy: ProductsPage },
      {
        path: 'product/:slug',
        lazy: ProductPage,
        // Loader extracts the slug so ProductPage can use useLoaderData()
        loader: ({ params }) => ({ slug: params.slug as string }),
      },
      { path: 'cart', lazy: CartPage },
      { path: 'checkout', lazy: CheckoutPage },
      // Build Your Own Merch configurator (T3767 — P1-E31-W50)
      { path: 'design', lazy: BYOMPage },
      // Full design catalog with line + branch filtering
      { path: 'catalog', lazy: CatalogPage },
      // Guest order tracking by lookup_token (E7-T05) — no auth required
      {
        path: 'track/:token',
        lazy: TrackPage,
        loader: ({ params }) => ({ token: params.token as string }),
      },

      // ── Account (auth-guarded in component) ──────────────────────
      { path: 'account', lazy: AccountPage },

      // ── Orders (auth-guarded in component) ───────────────────────
      { path: 'orders', lazy: OrdersPage },
      {
        path: 'orders/:id',
        lazy: OrderDetailPage,
        loader: ({ params }) => ({ id: params.id as string }),
      },
      {
        path: 'order/:id',
        lazy: OrderConfirmPage,
        loader: ({ params }) => ({ id: params.id as string }),
      },

      // ── Admin (auth-guarded in component) ────────────────────────
      { path: 'admin/catalog', lazy: AdminCatalogPage },
      { path: 'admin/byom/catalog', lazy: AdminBYOMCatalogPage },
      { path: 'admin/byom/elements', lazy: AdminElementsPage },
      { path: 'admin/channels', lazy: AdminChannelsPage },
      { path: 'admin/orders', lazy: AdminOrdersPage },
      {
        path: 'admin/orders/:id',
        lazy: AdminOrderDetailPage,
        loader: ({ params }) => ({ id: params.id as string }),
      },

      // ── Legal ─────────────────────────────────────────────────────
      { path: 'privacy', lazy: PrivacyPage },
      { path: 'terms', lazy: TermsPage },
      { path: 'refund', lazy: RefundPage },
      { path: 'shipping', lazy: ShippingPage },
      { path: 'returns', lazy: ReturnsPage },
      // Consent / cookie preferences (GDPR Art. 7(3) + CCPA §1798.135)
      { path: 'consent', lazy: ConsentPage },
      { path: 'contact', lazy: ContactPage },
      // Legal redirect routes — proxy pages that live on veterans.pro (canonical)
      {
        path: 'legal/privacy',
        lazy: () =>
          ExternalRedirectPage().then(({ Component: C }) => ({
            Component: () => <C url="https://veterans.pro/legal/privacy" />,
          })),
      },
      {
        path: 'legal/terms',
        lazy: () =>
          ExternalRedirectPage().then(({ Component: C }) => ({
            Component: () => <C url="https://veterans.pro/legal/terms" />,
          })),
      },
      {
        path: 'legal/accessibility',
        lazy: () =>
          ExternalRedirectPage().then(({ Component: C }) => ({
            Component: () => <C url="https://veterans.pro/legal/accessibility" />,
          })),
      },
      {
        // Cookie preferences live in the shop's own consent manager (the external
        // veterans.pro/legal/cookies page 404s). Render the local ConsentPage.
        path: 'legal/cookies',
        lazy: ConsentPage,
      },
    ],
  },
])
